/**
 * ProfileDetail — Profile page with three tabs: Operating Manual (renders the
 * generated Blueprint), Notes + Tags (manager edits), Settings (identity edit).
 *
 * V1 first ship: tab-based layout. Operating Manual generation triggers from
 * here; the rendered output streams into the OperatingManualPage component.
 */

import { useEffect, useState, startTransition } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useSession } from "../lib/useSession.js";
import { SectionHeader, Callout, Chip, CheckList, LiteInvitePanel, BlueprintJobView } from "../components/index.js";
import { parseJsonResponse } from "../lib/api-fetch.js";
import OperatingManualPage from "./OperatingManualPage.jsx";
import ComposePage from "./ComposePage.jsx";

// Display labels for observed-tag categories. The DB check constraint keeps
// the original stored values (communication, decision_making, stress_response,
// collaboration, growth_edge, freeform) — relabel at the UI layer only, same
// posture as tierDisplayLabel in feature-gates.js. Sales register: how they
// communicate, how they decide, how they act under pressure, how they deal
// with people in the room, and what they're reaching for next.
const TAG_CATEGORY_LABELS = {
  freeform: "freeform",
  communication: "communication",
  decision_making: "decision making",
  stress_response: "under pressure",
  collaboration: "dealing with people",
  growth_edge: "reaching for",
};

export default function ProfileDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { session, appUser } = useSession();
  const [profile, setProfile] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState("manual");
  const [manualData, setManualData] = useState(null);
  // Streaming is the only path. Sync generation reliably exceeds Netlify's
  // 26s function timeout (OM p50 ~80s per /shared/V1_Latency_Measurement.md),
  // so the Standard button was a guaranteed 504 trap. Removed 2026-05-07
  // (bug #46). The streaming surface hands off to the same OperatingManualPage
  // on done, so the final render is identical.
  const [tags, setTags] = useState([]);
  const [notes, setNotes] = useState([]);

  useEffect(() => {
    if (!session || !id) return;
    fetch(`/.netlify/functions/profiles/${id}`, {
      headers: { authorization: `Bearer ${session.access_token}` },
    })
      .then(async (r) => ({ res: r, body: await parseJsonResponse(r) }))
      .then(({ res, body }) => {
        if (!res.ok) setError(body.error || `Failed to load (HTTP ${res.status})`);
        else if (body.error) setError(body.error);
        else setProfile(body);
      })
      .catch((e) => setError(e.message));
  }, [session, id]);

  // Load the latest persisted Operating Manual for this profile on mount,
  // so the user sees their previously-generated OM instead of the
  // "Generate" button every time they navigate back. Bug #74 (Russ
  // 2026-05-09): "if I have generated a profile for Jimmy, shouldn't I
  // be able to see that last saved version?" — yes, and now we do.
  // Fires once per (session, id) pair. 404 from the endpoint is the
  // normal "no OM yet" path — leaves manualData null so the Generate
  // button renders.
  useEffect(() => {
    if (!session || !id) return;
    let active = true;
    fetch(`/.netlify/functions/operating-manual-latest?profile_id=${encodeURIComponent(id)}&viewer_mode=manager`, {
      headers: { authorization: `Bearer ${session.access_token}` },
    })
      .then(async (r) => ({ res: r, body: await parseJsonResponse(r) }))
      .then(({ res, body }) => {
        if (!active) return;
        if (res.status === 404) return;          // no OM yet — leave manualData null
        if (!res.ok) return;                     // endpoint hiccup; don't block the page
        // Map the endpoint shape into what OperatingManualPage expects
        // (matches the shape used by the BlueprintJobView onComplete handler).
        setManualData({
          blueprint_id: body.blueprint_id,
          operating_manual_markdown: body.operating_manual_markdown,
          hedge_phrases: body.hedge_phrases || [],
          jargon_scan_passed: body.jargon_scan_passed,
          engine_version: body.engine_version,
          generated_at: body.generated_at,
        });
      })
      .catch(() => { /* silent — falls back to Generate button */ });
    return () => { active = false; };
  }, [session, id]);

  useEffect(() => {
    if (!session || !id || tab !== "notes") return;
    Promise.all([
      fetch(`/.netlify/functions/profile-tags?profile_id=${id}`, {
        headers: { authorization: `Bearer ${session.access_token}` },
      }).then(parseJsonResponse),
      fetch(`/.netlify/functions/profile-notes?profile_id=${id}`, {
        headers: { authorization: `Bearer ${session.access_token}` },
      }).then(parseJsonResponse),
    ])
      .then(([t, n]) => {
        if (t.error) setError(t.error);
        else if (n.error) setError(n.error);
        else { setTags(t.tags || []); setNotes(n.notes || []); }
      })
      .catch((e) => setError(e.message));
  }, [session, id, tab]);

  // generateManual sync was removed 2026-05-07 (bug #46). The streaming
  // path via <StreamingMarkdown> is the only generation route. See the
  // ManualTab branch below for the wiring.

  if (error && !profile) {
    return (
      <div style={{ padding: 24 }}>
        <p style={{ color: "var(--ppr-color-accent-coral-dark)" }}>{error}</p>
      </div>
    );
  }
  if (!profile) return <p style={{ padding: 24 }}>Loading…</p>;

  return (
    <div style={{ maxWidth: 1000, margin: "0 auto", padding: "32px 24px" }}>
      <div style={{ marginBottom: 24 }}>
        <Link
          to="/profiles"
          style={{ fontFamily: "var(--ppr-typography-font-family-mono)", fontSize: "12px", color: "var(--ppr-color-text-muted)", textDecoration: "none" }}
        >
          ← all profiles
        </Link>
      </div>

      <header style={{ marginBottom: 24 }}>
        {/*
          Predisposition Profile eyebrow — V3 Decision Science noun rollout
          (Phase 1.7, 2026-05-19). Surfaces the formal product noun ONCE at the
          top of the detail page; body content thereafter uses the short
          "Profile" form. Coral 11px uppercase 0.10em matches the v10
          SectionHeader / Methodology page hero eyebrow grammar. Reference
          mockup: /design/mockups/Phase3_OperatingManual_Full.html (Marcus
          Liang profile identity block).
        */}
        <div
          data-testid="predisposition-profile-eyebrow"
          style={{
            fontFamily: "var(--ppr-typography-font-family-ui)",
            fontSize: 11,
            letterSpacing: "0.16em",
            textTransform: "uppercase",
            color: "var(--ppr-color-accent-coral)",
            marginBottom: 6,
          }}
        >
          The read on
        </div>
        <h1 style={{
          fontFamily: "var(--ppr-typography-font-family-display)",
          fontSize: "var(--ppr-typography-font-size-3xl)",
          fontWeight: "var(--ppr-typography-font-weight-regular)",
          color: "var(--ppr-color-text-primary)",
          letterSpacing: "var(--ppr-typography-letter-spacing-snug)",
          margin: "0 0 6px",
          lineHeight: "var(--ppr-typography-line-height-tight)",
        }}>
          {profile.display_name}
        </h1>
        <div style={{ fontFamily: "var(--ppr-typography-font-family-body)", color: "var(--ppr-color-text-secondary)" }}>
          {profile.role_title || "—"}{profile.department ? ` · ${profile.department}` : ""}{profile.work_email ? ` · ${profile.work_email}` : ""}
        </div>
        <div style={{ marginTop: 8 }}>
          <ModePill mode={profile.mode} />
        </div>
      </header>

      <nav style={{ display: "flex", gap: 12, marginBottom: 24 }}>
        <Chip primary={tab === "manual"} onClick={() => setTab("manual")}>Playbook</Chip>
        <Chip primary={tab === "compose"} onClick={() => setTab("compose")} data-testid="tab-compose">Compose</Chip>
        <Chip primary={tab === "notes"} onClick={() => setTab("notes")}>Notes & Tags</Chip>
      </nav>

      {tab === "manual" && (
        <ManualTab
          profile={profile}
          manualData={manualData}
          setManualData={setManualData}
          session={session}
          profileId={id}
        />
      )}

      {tab === "compose" && (
        <ComposePage
          profile={profile}
          profileId={id}
          session={session}
        />
      )}

      {tab === "notes" && (
        <NotesTagsTab
          profileId={id}
          profile={profile}
          tags={tags}
          notes={notes}
          session={session}
          onChange={() => setTab("notes")}
        />
      )}

      {error && profile && (
        <div style={{
          marginTop: 24,
          padding: "12px 14px",
          border: "2px solid var(--ppr-color-accent-coral)",
          background: "var(--ppr-color-accent-coral-very-soft)",
          color: "var(--ppr-color-accent-coral-dark)",
        }}>
          {error}
        </div>
      )}

      {/* Danger zone — delete this profile (cascades notes, tags, history). */}
      <div style={{
        marginTop: 44,
        paddingTop: 16,
        borderTop: "1px solid var(--ppr-color-border-default)",
        display: "flex",
        justifyContent: "flex-end",
      }}>
        <button
          onClick={async () => {
            const name = profile?.display_name || "this profile";
            if (!window.confirm(`Delete ${name}? This removes the profile and everything attached to it (notes, tags, history). This can't be undone.`)) return;
            setDeleting(true);
            try {
              const res = await fetch(`/.netlify/functions/profiles/${id}`, {
                method: "DELETE",
                headers: { authorization: `Bearer ${session.access_token}` },
              });
              const body = await parseJsonResponse(res);
              if (!res.ok) {
                setError(body.error || `Delete failed (HTTP ${res.status})`);
                setDeleting(false);
                return;
              }
              navigate("/profiles");
            } catch (e) {
              setError(e.message);
              setDeleting(false);
            }
          }}
          disabled={deleting}
          style={{
            padding: "10px 14px",
            fontFamily: "var(--ppr-typography-font-family-display)",
            fontSize: "var(--ppr-typography-font-size-sm)",
            letterSpacing: "var(--ppr-typography-letter-spacing-wider)",
            textTransform: "uppercase",
            border: "2px solid var(--ppr-color-accent-coral)",
            background: "transparent",
            color: "var(--ppr-color-accent-coral-dark)",
            cursor: deleting ? "wait" : "pointer",
            opacity: deleting ? 0.7 : 1,
          }}
        >
          {deleting ? "Deleting…" : "Delete profile"}
        </button>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────

function ManualTab({
  profile, manualData, setManualData,
  session, profileId,
}) {
  const [generationStarted, setGenerationStarted] = useState(false);

  if (profile.mode === "Lite") {
    return (
      <LiteInvitePanel
        profileId={profileId}
        profile={profile}
        bearerToken={session?.access_token}
      />
    );
  }

  // Tier 2 (background+polling) generation per /shared/V1_OM_TeamBlueprint_Background_Polling_Relock.md
  // (locked 2026-05-07). Replaces the old streaming-OM path which couldn't
  // fit into Netlify's V2 streaming runtime cap.
  if (generationStarted && !manualData) {
    return (
      <BlueprintJobView
        bearerToken={session?.access_token}
        surfaceLabel="Playbook"
        onRetry={() => {
          // Cycle generationStarted through false → true so BlueprintJobView
          // unmounts + remounts, re-firing its start() effect with a fresh
          // job_id. Mirrors the streaming retry pattern from #62 and the
          // CM page's retryJob handler. Bug #72.
          setGenerationStarted(false);
          setTimeout(() => setGenerationStarted(true), 0);
        }}
        start={async () => {
          const res = await fetch("/.netlify/functions/operating-manual-start", {
            method: "POST",
            headers: {
              "content-type": "application/json",
              authorization: `Bearer ${session?.access_token}`,
            },
            body: JSON.stringify({ profile_id: profileId, viewer_mode: "manager" }),
          });
          const body = await parseJsonResponse(res);
          if (!res.ok) {
            return { ok: false, error: body.error || `Failed to start (HTTP ${res.status})` };
          }
          return { ok: true, job_id: body.job_id, estimated_total_seconds: body.estimated_total_seconds };
        }}
        onComplete={(_blueprintId, jobRow) => {
          // The status endpoint inlines the blueprint row when status flips
          // to complete (saves a second round-trip). Map its shape into
          // what OperatingManualPage expects (operating_manual_markdown +
          // metadata fields).
          //
          // setManualData is wrapped in startTransition for the same reason
          // as ConflictMediationBriefPage's onComplete (bug #66, 2026-05-09):
          // the post-completion render of the polished <Markdown> component
          // saturates the main thread on long blueprints (~16-25K chars).
          // Without startTransition, the synchronous state update triggers
          // an immediate unmount-of-BlueprintJobView + mount-of-OperatingManualPage
          // in one commit, and the heavy parse hangs the page (RESULT_CODE_HUNG).
          // With startTransition, the parse runs in idle time and the
          // browser stays responsive. BlueprintJobView's "Generation complete
          // — loading blueprint…" message is visible during the deferred
          // commit.
          const bp = jobRow?.blueprint;
          if (bp) {
            startTransition(() => {
              setManualData({
                blueprint_id: bp.blueprint_id,
                operating_manual_markdown: bp.rendered_markdown,
                hedge_phrases: bp.hedge_phrases || [],
                jargon_scan_passed: bp.jargon_scan_passed,
                engine_version: bp.layer_4_engine_version,
                generated_at: bp.generated_at,
              });
            });
          }
        }}
      />
    );
  }

  if (!manualData) {
    return (
      <div>
        <Callout.Tip>
          The Playbook renders {profile.display_name}'s full behavioral read —
          how they decide, what to lead with, the trap, and your move.
          Generation typically completes under 90 seconds; we stay below 2
          minutes for 95% of runs. You can cancel any time.
        </Callout.Tip>
        <button
          onClick={() => setGenerationStarted(true)}
          style={{
            marginTop: 16,
            padding: "12px 18px",
            fontFamily: "var(--ppr-typography-font-family-ui)",
            fontSize: "var(--ppr-typography-font-size-sm)",
            letterSpacing: "0.04em",
            border: "1px solid var(--ppr-color-accent-navy)",
            borderRadius: "var(--ppr-radius-sm)",
            background: "var(--ppr-color-accent-navy)",
            color: "var(--ppr-color-text-inverse)",
            cursor: "pointer",
          }}
        >
          Generate Playbook
        </button>
      </div>
    );
  }
  return (
    <OperatingManualPage
      data={manualData}
      bearerToken={session?.access_token}
    />
  );
}

function NotesTagsTab({ profileId, profile, tags, notes, session, onChange }) {
  const [tagText, setTagText] = useState("");
  const [tagCategory, setTagCategory] = useState("freeform");
  const [noteBody, setNoteBody] = useState("");
  const [noteVisibility, setNoteVisibility] = useState("manager_private");
  const [busy, setBusy] = useState(false);

  const addTag = async () => {
    if (!tagText) return;
    setBusy(true);
    await fetch("/.netlify/functions/profile-tags", {
      method: "POST",
      headers: { "content-type": "application/json", authorization: `Bearer ${session.access_token}` },
      body: JSON.stringify({ profile_id: profileId, text: tagText, category: tagCategory }),
    });
    setTagText("");
    onChange();
    setBusy(false);
  };

  const addNote = async () => {
    if (!noteBody) return;
    setBusy(true);
    await fetch("/.netlify/functions/profile-notes", {
      method: "POST",
      headers: { "content-type": "application/json", authorization: `Bearer ${session.access_token}` },
      body: JSON.stringify({ profile_id: profileId, body: noteBody, visibility: noteVisibility }),
    });
    setNoteBody("");
    onChange();
    setBusy(false);
  };

  return (
    <div style={{ display: "grid", gap: 32 }}>
      {/*
        Note: the Send Invite affordance for Lite profiles lives on the
        Operating Manual tab (the LiteInvitePanel inside the Remember
        callout) per /design/V1_Send_Invite_Pattern_Spec.md. Earlier
        revisions (bug #38, 2026-05-05) duplicated it here too — that
        was superseded by the locked single-placement pattern on
        2026-05-06. Don't re-add it.
      */}
      <section>
        <SectionHeader index="∗" title="Observed-behavior tags" />
        <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
          <input
            value={tagText}
            onChange={(e) => setTagText(e.target.value)}
            placeholder="e.g. decisive under pressure"
            style={{
              flex: "2 1 280px",
              padding: "10px 12px",
              fontFamily: "var(--ppr-typography-font-family-ui)",
              fontSize: "var(--ppr-typography-font-size-base)",
              border: "1px solid var(--ppr-color-border-default)",
              background: "var(--ppr-color-background-surface)",
            }}
          />
          <select
            value={tagCategory}
            onChange={(e) => setTagCategory(e.target.value)}
            style={{
              padding: "10px 12px",
              fontFamily: "var(--ppr-typography-font-family-ui)",
              border: "1px solid var(--ppr-color-border-default)",
              background: "var(--ppr-color-background-surface)",
            }}
          >
            {Object.entries(TAG_CATEGORY_LABELS).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
          <button onClick={addTag} disabled={busy} style={addBtn}>Add tag</button>
        </div>
        {tags.length === 0 ? (
          <p style={{ color: "var(--ppr-color-text-muted)" }}>No tags yet.</p>
        ) : (
          <CheckList items={tags.map((t) => (
            <span key={t.tag_id}>
              <strong>{t.text}</strong>
              <span style={{ color: "var(--ppr-color-text-muted)", marginLeft: 8 }}>
                · {TAG_CATEGORY_LABELS[t.category] || t.category} · {new Date(t.created_at).toLocaleDateString()}
              </span>
            </span>
          ))} />
        )}
      </section>

      <section>
        <SectionHeader index="∗" title="Notes" />
        <div style={{ display: "grid", gap: 8, marginBottom: 14 }}>
          <textarea
            value={noteBody}
            onChange={(e) => setNoteBody(e.target.value)}
            rows={3}
            placeholder="What surprised you? What pattern is emerging?"
            style={{
              padding: "10px 12px",
              fontFamily: "var(--ppr-typography-font-family-body)",
              fontSize: "var(--ppr-typography-font-size-base)",
              lineHeight: "var(--ppr-typography-line-height-relaxed)",
              border: "1px solid var(--ppr-color-border-default)",
              background: "var(--ppr-color-background-surface)",
              resize: "vertical",
            }}
          />
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={addNote} disabled={busy} style={addBtn}>Add note</button>
          </div>
        </div>
        {notes.length === 0 ? (
          <p style={{ color: "var(--ppr-color-text-muted)" }}>No notes yet.</p>
        ) : (
          notes.map((n) => (
            <article
              key={n.note_id}
              style={{
                padding: "12px 14px",
                marginBottom: 10,
                border: "1px solid var(--ppr-color-border-default)",
                borderRadius: "var(--ppr-radius-sm)",
                background: "var(--ppr-color-background-surface)",
              }}
            >
              <div style={{ fontFamily: "var(--ppr-typography-font-family-mono)", fontSize: "11px", color: "var(--ppr-color-text-muted)", marginBottom: 6 }}>
                {new Date(n.created_at).toLocaleString()}
              </div>
              <div style={{ fontFamily: "var(--ppr-typography-font-family-body)", lineHeight: "var(--ppr-typography-line-height-relaxed)" }}>
                {n.body}
              </div>
            </article>
          ))
        )}
      </section>
    </div>
  );
}

const addBtn = {
  padding: "10px 18px",
  fontFamily: "var(--ppr-typography-font-family-display)",
  fontSize: "var(--ppr-typography-font-size-sm)",
  letterSpacing: "var(--ppr-typography-letter-spacing-wider)",
  textTransform: "uppercase",
  border: "1px solid var(--ppr-color-border-default)",
  background: "var(--ppr-color-accent-navy)",
  color: "var(--ppr-color-text-inverse)",
  cursor: "pointer",
};

function ModePill({ mode }) {
  const styles = {
    Lite: { bg: "var(--ppr-color-background-muted)", fg: "var(--ppr-color-text-muted)" },
    Full: { bg: "var(--ppr-color-accent-pale-soft)", fg: "var(--ppr-color-accent-navy)" },
    Departed: { bg: "var(--ppr-color-background-muted)", fg: "var(--ppr-color-text-secondary)" },
    LegalHold: { bg: "var(--ppr-color-accent-coral-very-soft)", fg: "var(--ppr-color-accent-coral-dark)" },
  }[mode] || { bg: "var(--ppr-color-background-muted)", fg: "var(--ppr-color-text-muted)" };
  return (
    <span style={{
      padding: "4px 10px",
      background: styles.bg,
      color: styles.fg,
      fontFamily: "var(--ppr-typography-font-family-display)",
      fontSize: "11px",
      letterSpacing: "var(--ppr-typography-letter-spacing-wider)",
      textTransform: "uppercase",
    }}>
      {mode}
    </span>
  );
}
